import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'common/store';
import { FieldAttribute } from 'common/types/store';
import { UseStoreValues } from 'common/types/store/store';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import afterCellChanged from './afterCellChanged';
import conditions from './conditions';
import parser from './parser';
import reverseParser from './reverseParser';
import rowConfig from './rowConfig';
import { ReverseParsedDividendRateLedgerItem } from './types';
import validations from './validations';

const useDividendRateLedger = ({
  ledgerData,
  isDisabled = false,
  cellColumnRef,
  securities,
  divRateLedgerItemAttrs,
  measurementDate,
}: {
  ledgerData: ReverseParsedDividendRateLedgerItem[];
  isDisabled: boolean;
  cellColumnRef: string;
  securities: { columnRef: string; dividend_rate_ledger_items: ReverseParsedDividendRateLedgerItem[] }[];
  divRateLedgerItemAttrs: FieldAttribute;
  measurementDate: string;
}) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { format } = storeValue;
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetConfig | null>(null);
  const [columns, setColumns] = useState<ReverseParsedDividendRateLedgerItem[]>(ledgerData);

  const config = useMemo(() => rowConfig(), []);

  useEffect(() => {
    if (columns.length && config.length) {
      const tableTerms = {
        tableName: 'Multiple Dividend Rates Ledger',
        tableSlug: 'dividend-rate-ledger',
        columnName: 'dividend rate',
      };
      setSpreadsheet(
        new SpreadsheetConfig({
          columns,
          name: 'DividendRateLedger',
          totalParser: undefined,
          conditions: conditions as any,
          fieldAttributes: divRateLedgerItemAttrs,
          tableData: {
            columns,
            isDisabled,
            cellColumnRef,
            securities,
            measurementDate,
          },
          afterCellChanged: afterCellChanged as any,
          parser,
          reverseParser: reverseParser as any,
          customValidations: validations as any,
          rowConfig: config,
          format,
          allowConfirmAndDeleteColumn: false,
          allowDeleteColumn: true,
          tableTerms: tableTerms as any,
          showToolbar: false,
          currencyFormatter: false,
          unitsFormatter: false,
        })
      );
    }
  }, [columns, config, isDisabled, format, cellColumnRef, divRateLedgerItemAttrs, securities, measurementDate]);

  return {
    spreadsheet,
    columns,
    setColumns,
  };
};

export default useDividendRateLedger;
